/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { GoodDate } from '@utils/datetime';
import httpRequest from '@utils/http-request';
import { asRecord, asString, asNumber, asArray, asNullableNumber, asBoolean } from '@models/json-utils';

const API_BASE = `${import.meta.env.VITE_SERVER_ENDPOINT}/contacts`;

export class Contact {
    id: string;
    firstName: string;
    lastName: string;
    familyName: string;
    altName: string;
    nickName: string;
    birthday?: GoodDate;
    identityNumber: string;
    nationality: string;
    cityOfResidence: string;
    careerField: string;
    degree: string;
    description: string;
    notes: string;
    favourite: boolean;
    createdAt: GoodDate;
    updatedAt: GoodDate;
    deletedAt?: GoodDate;
    tags: ContactTag[];

    constructor(
        id: string,
        firstName: string,
        lastName: string,
        familyName: string,
        altName: string,
        nickName: string,
        birthday: number | null,
        description: string,
        notes: string,
        favourite: boolean,
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        tags: ContactTag[] = [],
        identityNumber = '',
        nationality = '',
        cityOfResidence = '',
        careerField = '',
        degree = ''
    ) {
        this.id = id;
        this.firstName = firstName;
        this.lastName = lastName;
        this.familyName = familyName;
        this.altName = altName;
        this.nickName = nickName;
        this.birthday = birthday !== null ? GoodDate.fromEpochSeconds(birthday) : undefined;
        this.identityNumber = identityNumber;
        this.nationality = nationality;
        this.cityOfResidence = cityOfResidence;
        this.careerField = careerField;
        this.degree = degree;
        this.description = description;
        this.notes = notes;
        this.favourite = favourite;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt) : undefined;
        this.tags = tags;
    }

    static fromJSON(json: unknown): Contact {
        const j = asRecord(json);
        return new Contact(
            asString(j.id),
            asString(j.firstName),
            asString(j.lastName),
            asString(j.familyName),
            asString(j.altName),
            asString(j.nickName),
            (j.birthday as number) ?? null,
            asString(j.description),
            asString(j.notes),
            asBoolean(j.favourite),
            asNumber(j.createdAt),
            asNumber(j.updatedAt),
            asNullableNumber(j.deletedAt) ?? undefined,
            ContactTag.fromJSONList(j.tags),
            asString(j.identityNumber),
            asString(j.nationality),
            asString(j.cityOfResidence),
            asString(j.careerField),
            asString(j.degree)
        );
    }

    static fromJSONList(json: unknown): Contact[] {
        return asArray(json, (item) => Contact.fromJSON(item));
    }

    static template = new Contact(
        '',
        '',
        '',
        '',
        '',
        '',
        null,
        '',
        '',
        false,
        GoodDate.nowInEpochSeconds(),
        GoodDate.nowInEpochSeconds()
    );

    static async list(props: {
        limit?: number;
        search?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        favourite?: boolean;
        upcomingBirthdayDays?: number;
        tagIds?: string[];
        untagged?: boolean;
        has?: ContactInformationFilter;
    }): Promise<Contact[]> {
        const params = new URLSearchParams();
        if (props.limit !== undefined) params.set('limit', props.limit.toString());
        if (props.search) params.set('search', props.search);
        if (props.sort) params.set('sort', props.sort);
        if (props.order) params.set('order', props.order);
        if (props.favourite !== undefined) params.set('favourite', props.favourite ? 'true' : 'false');
        if (props.upcomingBirthdayDays !== undefined)
            params.set('upcomingBirthdayDays', props.upcomingBirthdayDays.toString());
        props.tagIds?.forEach((tagId) => params.append('tag', tagId));
        if (props.untagged) params.set('untagged', 'true');
        if (props.has) params.set('has', props.has);
        const query = params.toString();
        const response = await httpRequest(`${API_BASE}${query ? `?${query}` : ''}`, 'GET'),
            json = await response.json();
        if (response.ok) {
            return Contact.fromJSONList(json.data);
        } else {
            throw new Error(json.error);
        }
    }

    static async listByRecentBirthday(): Promise<Contact[]> {
        const response = await httpRequest(`${API_BASE}/recent-birthday`, 'GET'),
            json = await response.json();
        if (response.ok) {
            return Contact.fromJSONList(json.data);
        } else {
            throw new Error(json.error);
        }
    }

    static async get(id: string): Promise<GetContactResponse> {
        const response = await httpRequest(`${API_BASE}/${id}`, 'GET'),
            json = await response.json();
        if (response.ok) {
            return GetContactResponse.fromJSON(json.data);
        } else {
            throw new Error(json.error);
        }
    }

    static async create(contact: SaveContactRequest): Promise<Contact> {
        const response = await httpRequest(`${API_BASE}/new`, 'PUT', contact),
            json = await response.json();
        if (response.ok) {
            return Contact.fromJSON(json.data);
        } else {
            throw new Error(json.error);
        }
    }

    static async save(id: string, contact: SaveContactRequest): Promise<Contact> {
        const response = await httpRequest(`${API_BASE}/${id}`, 'PUT', contact),
            json = await response.json();
        if (response.ok) {
            return Contact.fromJSON(json.data);
        } else {
            throw new Error(json.error);
        }
    }

    static async updatePartial(
        id: string,
        payload: Partial<Pick<SaveContactRequest, 'favourite'>> & Record<string, unknown>
    ): Promise<Contact> {
        const response = await httpRequest(`${API_BASE}/${id}`, 'PUT', payload),
            json = await response.json();
        if (response.status === 200) {
            return Contact.fromJSON(json.data);
        } else {
            throw new Error(json.error ?? 'Failed to update contact');
        }
    }

    static async setFavourite(id: string, favourite: boolean): Promise<Contact> {
        return Contact.updatePartial(id, { favourite });
    }

    static async setTags(id: string, tagIds: string[]): Promise<ContactTag[]> {
        const response = await httpRequest(`${API_BASE}/${id}/tags`, 'PUT', { tagIds });
        const json = await response.json();
        if (!response.ok) throw new Error(json.message ?? json.error ?? 'Failed to update contact labels');
        return ContactTag.fromJSONList(json.data);
    }

    static async delete(id: string): Promise<void> {
        const response = await httpRequest(`${API_BASE}/${id}`, 'DELETE');
        if (response.status !== 200) {
            const json = await response.json();
            throw new Error(json.error);
        }
    }
}

export type ContactInformationFilter =
    | 'phone'
    | 'email'
    | 'address'
    | 'im'
    | 'website'
    | 'birthday'
    | 'identity'
    | 'nationality'
    | 'city'
    | 'career'
    | 'degree'
    | 'education'
    | 'contactMethod'
    | 'missingContactMethod';

export interface SaveContactTagRequest {
    name: string;
    description: string;
    color: string;
}

export class ContactTag {
    id: string;
    name: string;
    description: string;
    color: string;
    createdAt: GoodDate;
    updatedAt: GoodDate;

    constructor(id: string, name: string, description: string, color: string, createdAt: number, updatedAt: number) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.color = color;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
    }

    static fromJSON(this: void, value: unknown): ContactTag {
        const json = asRecord(value);
        return new ContactTag(
            asString(json.id),
            asString(json.name),
            asString(json.description),
            asString(json.color, '#64748B'),
            asNumber(json.createdAt),
            asNumber(json.updatedAt)
        );
    }

    static fromJSONList(value: unknown): ContactTag[] {
        return asArray(value, ContactTag.fromJSON);
    }

    static async list(): Promise<ContactTag[]> {
        const response = await httpRequest(`${API_BASE}/tags`, 'GET');
        const json = await response.json();
        if (!response.ok) throw new Error(json.message ?? json.error ?? 'Failed to load contact labels');
        return ContactTag.fromJSONList(json.data);
    }

    static async create(payload: SaveContactTagRequest): Promise<ContactTag> {
        const response = await httpRequest(`${API_BASE}/tags`, 'POST', payload);
        const json = await response.json();
        if (response.status !== 201) throw new Error(json.message ?? json.error ?? 'Failed to create contact label');
        return ContactTag.fromJSON(json.data);
    }

    static async save(id: string, payload: SaveContactTagRequest): Promise<ContactTag> {
        const response = await httpRequest(`${API_BASE}/tags/${encodeURIComponent(id)}`, 'PUT', payload);
        const json = await response.json();
        if (!response.ok) throw new Error(json.message ?? json.error ?? 'Failed to update contact label');
        return ContactTag.fromJSON(json.data);
    }

    static async delete(id: string): Promise<void> {
        const response = await httpRequest(`${API_BASE}/tags/${encodeURIComponent(id)}`, 'DELETE');
        const json = await response.json();
        if (!response.ok) throw new Error(json.message ?? json.error ?? 'Failed to delete contact label');
    }
}

export interface ContactAddressInit {
    contactId?: string;
    line1?: string;
    line2?: string;
    district?: string;
    city?: string;
    province?: string;
    country?: string;
    postalCode?: string;
    type?: number;
    description?: string;
}

export class ContactAddress {
    contactId?: string;
    line1: string;
    line2?: string;
    district?: string;
    city?: string;
    province?: string;
    country?: string;
    postalCode?: string;
    type: number;
    description: string;

    static typeOptions = ['Home', 'Work', 'School', 'Other'];

    constructor(init: ContactAddressInit = {}) {
        this.contactId = init.contactId;
        this.line1 = init.line1 ?? '';
        this.line2 = init.line2;
        this.district = init.district;
        this.city = init.city ?? '';
        this.province = init.province;
        this.country = init.country ?? '';
        this.postalCode = init.postalCode ?? '';
        this.type = init.type ?? 0;
        this.description = init.description ?? '';
    }

    getTypeDescription() {
        return ContactAddress.typeOptions[this.type] ?? ContactAddress.typeOptions[0];
    }

    formatSingleLine() {
        const normalize = (value?: string) => (value ? value.trim() : '');
        const parts = [
            normalize(this.line1),
            normalize(this.line2),
            normalize(this.district),
            normalize(this.city),
            normalize(this.province),
            normalize(this.country),
            normalize(this.postalCode),
        ].filter((part) => part.length > 0);
        return parts.join(', ');
    }

    static fromJSON(json: unknown): ContactAddress {
        const j = asRecord(json);
        return new ContactAddress({
            contactId: asString(j.contactId),
            line1: asString(j.line1) ?? asString(j.address) ?? '',
            line2: asString(j.line2) ?? asString(j.addressLine2) ?? undefined,
            district: asString(j.district) ?? undefined,
            city: asString(j.city),
            province: asString(j.province) ?? asString(j.region) ?? undefined,
            country: asString(j.country),
            postalCode: asString(j.postalCode),
            type: (j.type as number) ?? 0,
            description: asString(j.description),
        });
    }
    static fromJSONList(json: unknown): ContactAddress[] {
        return asArray(json, (item) => ContactAddress.fromJSON(item));
    }
}

export class ContactPhone {
    contactId: string;
    phone: string;
    type: number;
    description: string;

    static typeOptions = ['Mobile', 'Home', 'Work', 'School', 'Other'];

    constructor(contactId: string, phone: string, type: number, description: string) {
        this.contactId = contactId;
        this.phone = phone;
        this.type = type;
        this.description = description;
    }

    getTypeDescription() {
        return ContactPhone.typeOptions[this.type];
    }

    static fromJSON(json: unknown): ContactPhone {
        const j = asRecord(json);
        return new ContactPhone(
            asString(j.contactId),
            asString(j.phone),
            (j.type as number) ?? 0,
            asString(j.description)
        );
    }

    static fromJSONList(json: unknown): ContactPhone[] {
        return asArray(json, (item) => ContactPhone.fromJSON(item));
    }
}

export class ContactEmail {
    contactId: string;
    email: string;
    type: number;
    description: string;

    static typeOptions = ['Personal', 'Work', 'School', 'Other'];

    constructor(contactId: string, email: string, type: number, description: string) {
        this.contactId = contactId;
        this.email = email;
        this.type = type;
        this.description = description;
    }

    getTypeDescription() {
        return ContactEmail.typeOptions[this.type];
    }

    static fromJSON(json: unknown): ContactEmail {
        const j = asRecord(json);
        return new ContactEmail(
            asString(j.contactId),
            asString(j.email),
            (j.type as number) ?? 0,
            asString(j.description)
        );
    }

    static fromJSONList(json: unknown): ContactEmail[] {
        return asArray(json, (item) => ContactEmail.fromJSON(item));
    }
}

export class ContactIM {
    contactId: string;
    im: string;
    type: number;
    description: string;

    static typeOptions = ['QQ', 'WeChat', 'Telegram', 'Twitter', 'Facebook', 'Instagram', 'Other'];

    constructor(contactId: string, im: string, type: number, description: string) {
        this.contactId = contactId;
        this.im = im;
        this.type = type;
        this.description = description;
    }

    getTypeDescription() {
        return ContactIM.typeOptions[this.type];
    }

    static fromJSON(json: unknown): ContactIM {
        const j = asRecord(json);
        return new ContactIM(asString(j.contactId), asString(j.im), (j.type as number) ?? 0, asString(j.description));
    }

    static fromJSONList(json: unknown): ContactIM[] {
        return asArray(json, (item) => ContactIM.fromJSON(item));
    }
}

export class ContactURL {
    contactId: string;
    url: string;
    type: number;
    description: string;

    static typeOptions = ['Personal', 'Work', 'School', 'Other'];

    constructor(contactId: string, url: string, type: number, description: string) {
        this.contactId = contactId;
        this.url = url;
        this.type = type;
        this.description = description;
    }

    getTypeDescription() {
        return ContactURL.typeOptions[this.type];
    }

    static fromJSON(json: unknown): ContactURL {
        const j = asRecord(json);
        return new ContactURL(asString(j.contactId), asString(j.url), (j.type as number) ?? 0, asString(j.description));
    }

    static fromJSONList(json: unknown): ContactURL[] {
        return asArray(json, (item) => ContactURL.fromJSON(item));
    }
}

export class ContactRelationship {
    contactId: string;
    relatedId: string;
    type: number;
    description: string;

    static typeOptions = ['Family', 'Friend', 'Colleague', 'Other'];

    constructor(contactId: string, relatedId: string, type: number, description: string) {
        this.contactId = contactId;
        this.relatedId = relatedId;
        this.type = type;
        this.description = description;
    }

    getTypeDescription() {
        return ContactRelationship.typeOptions[this.type];
    }

    static fromJSON(json: unknown): ContactRelationship {
        const j = asRecord(json);
        return new ContactRelationship(
            asString(j.contactId),
            asString(j.relatedId),
            (j.type as number) ?? 0,
            asString(j.description)
        );
    }

    static fromJSONList(json: unknown): ContactRelationship[] {
        return asArray(json, (item) => ContactRelationship.fromJSON(item));
    }
}

export interface ContactEducationBackgroundInit {
    id?: string;
    contactId?: string;
    institution?: string;
    degree?: string;
    fieldOfStudy?: string;
    startAt?: number | null;
    endAt?: number | null;
    description?: string;
}

export class ContactEducationBackground {
    id: string;
    contactId: string;
    institution: string;
    degree: string;
    fieldOfStudy: string;
    startAt?: GoodDate;
    endAt?: GoodDate;
    description: string;

    constructor(init: ContactEducationBackgroundInit = {}) {
        this.id = init.id ?? '';
        this.contactId = init.contactId ?? '';
        this.institution = init.institution ?? '';
        this.degree = init.degree ?? '';
        this.fieldOfStudy = init.fieldOfStudy ?? '';
        this.startAt = init.startAt != null ? GoodDate.fromEpochSeconds(init.startAt) : undefined;
        this.endAt = init.endAt != null ? GoodDate.fromEpochSeconds(init.endAt) : undefined;
        this.description = init.description ?? '';
    }

    static fromJSON(json: unknown): ContactEducationBackground {
        const j = asRecord(json);
        return new ContactEducationBackground({
            id: asString(j.id),
            contactId: asString(j.contactId),
            institution: asString(j.institution),
            degree: asString(j.degree),
            fieldOfStudy: asString(j.fieldOfStudy),
            startAt: asNullableNumber(j.startAt),
            endAt: asNullableNumber(j.endAt),
            description: asString(j.description),
        });
    }

    static fromJSONList(json: unknown): ContactEducationBackground[] {
        return asArray(json, (item) => ContactEducationBackground.fromJSON(item));
    }
}

export class GetContactResponse extends Contact {
    addresses: ContactAddress[];
    emails: ContactEmail[];
    ims: ContactIM[];
    phones: ContactPhone[];
    urls: ContactURL[];
    relationships: ContactRelationship[];
    educationBackgrounds: ContactEducationBackground[];

    constructor(
        contact: Contact,
        addresses: ContactAddress[],
        emails: ContactEmail[],
        ims: ContactIM[],
        phones: ContactPhone[],
        urls: ContactURL[],
        relationships: ContactRelationship[],
        educationBackgrounds: ContactEducationBackground[]
    ) {
        super(
            contact.id,
            contact.firstName,
            contact.lastName,
            contact.familyName,
            contact.altName,
            contact.nickName,
            contact.birthday?.toEpochSeconds() ?? null,
            contact.description,
            contact.notes,
            contact.favourite,
            contact.createdAt.toEpochSeconds(),
            contact.updatedAt.toEpochSeconds(),
            contact.deletedAt?.toEpochSeconds(),
            contact.tags,
            contact.identityNumber,
            contact.nationality,
            contact.cityOfResidence,
            contact.careerField,
            contact.degree
        );
        this.addresses = addresses;
        this.emails = emails;
        this.ims = ims;
        this.phones = phones;
        this.urls = urls;
        this.relationships = relationships;
        this.educationBackgrounds = educationBackgrounds;
    }

    static fromJSON(json: unknown): GetContactResponse {
        const j = json as Record<string, unknown>;
        return new GetContactResponse(
            Contact.fromJSON(j),
            ContactAddress.fromJSONList(j.addresses),
            ContactEmail.fromJSONList(j.emails),
            ContactIM.fromJSONList(j.ims),
            ContactPhone.fromJSONList(j.phones),
            ContactURL.fromJSONList(j.urls),
            ContactRelationship.fromJSONList(j.relationships),
            ContactEducationBackground.fromJSONList(j.educationBackgrounds)
        );
    }
}

export class SaveContactRequest {
    firstName: string;
    lastName: string;
    familyName: string;
    altName: string;
    nickName: string;
    birthday: number | null;
    description: string;
    notes: string;
    favourite: boolean;
    addresses: ContactAddress[];
    emails: ContactEmail[];
    ims: ContactIM[];
    phones: ContactPhone[];
    urls: ContactURL[];
    relationships: ContactRelationship[];
    tagIds: string[];
    identityNumber: string;
    nationality: string;
    cityOfResidence: string;
    careerField: string;
    degree: string;
    educationBackgrounds: ContactEducationBackground[];

    constructor(
        firstName: string,
        lastName: string,
        familyName: string,
        altName: string,
        nickName: string,
        birthday: number | null,
        description: string,
        notes: string,
        favourite: boolean,
        addresses: ContactAddress[],
        emails: ContactEmail[],
        ims: ContactIM[],
        phones: ContactPhone[],
        urls: ContactURL[],
        relationships: ContactRelationship[],
        tagIds: string[] = [],
        identityNumber = '',
        nationality = '',
        cityOfResidence = '',
        careerField = '',
        degree = '',
        educationBackgrounds: ContactEducationBackground[] = []
    ) {
        this.firstName = firstName;
        this.lastName = lastName;
        this.familyName = familyName;
        this.altName = altName;
        this.nickName = nickName;
        this.birthday = birthday;
        this.description = description;
        this.notes = notes;
        this.favourite = favourite;
        this.addresses = addresses;
        this.emails = emails;
        this.ims = ims;
        this.phones = phones;
        this.urls = urls;
        this.relationships = relationships;
        this.tagIds = tagIds;
        this.identityNumber = identityNumber;
        this.nationality = nationality;
        this.cityOfResidence = cityOfResidence;
        this.careerField = careerField;
        this.degree = degree;
        this.educationBackgrounds = educationBackgrounds;
    }
}
